import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  QrCode, Camera, CameraOff, Search, Package, ArrowDown, ArrowUp,
  ArrowLeftRight, ChevronRight, ChevronLeft, Check, X, MapPin,
  Upload, Loader2, RotateCcw, Minus, Plus, Box, AlertTriangle
} from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { useQrScanner } from '../hooks/useQrScanner';

const OP_CONFIG = {
  pick: { label: 'Preluare din Stoc', icon: ArrowUp, color: 'bg-red-600', hoverColor: 'hover:bg-red-700', lightBg: 'bg-red-50 border-red-200', textColor: 'text-red-700' },
  add: { label: 'Adaugare in Stoc', icon: ArrowDown, color: 'bg-emerald-600', hoverColor: 'hover:bg-emerald-700', lightBg: 'bg-emerald-50 border-emerald-200', textColor: 'text-emerald-700' },
  transfer: { label: 'Transfer intre Locatii', icon: ArrowLeftRight, color: 'bg-blue-600', hoverColor: 'hover:bg-blue-700', lightBg: 'bg-blue-50 border-blue-200', textColor: 'text-blue-700' },
};

const ScanHubPage = () => {
  // Wizard state
  const [step, setStep] = useState(0); // 0=scan, 1=operation, 2=config, 3=confirm
  const [article, setArticle] = useState(null);
  const [binsStock, setBinsStock] = useState([]);
  const [totalStock, setTotalStock] = useState(0);
  const [operation, setOperation] = useState(null); // pick, add, transfer
  const [allBins, setAllBins] = useState([]);

  // BIN scan state
  const [scannedBin, setScannedBin] = useState(null);
  const [binArticles, setBinArticles] = useState([]);

  // Config state
  const [fromBinId, setFromBinId] = useState('');
  const [toBinId, setToBinId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Camera
  const [cameraActive, setCameraActive] = useState(false);
  const { start: startQr, stop: stopQr } = useQrScanner();

  useEffect(() => {
    fetchAllBins();
    return () => { stopQr(); };
  }, []);

  const fetchAllBins = async () => {
    try {
      const resp = await api.get('/bins');
      setAllBins(resp.data);
    } catch { /* ignore */ }
  };

  // ---- CAMERA ----
  const startCamera = async () => {
    setCameraActive(true);
  };

  useEffect(() => {
    if (!cameraActive) return;
    let mounted = true;
    const initCamera = async () => {
      await new Promise(r => setTimeout(r, 400));
      if (!mounted) return;
      try {
        await startQr('stock-qr-reader', (text) => { stopCamera(); handleScanResult(text); }, { fps: 10, qrbox: { width: 220, height: 220 } });
      } catch {
        if (mounted) {
          toast.error('Nu se poate accesa camera. Verificati permisiunile.');
          setCameraActive(false);
        }
      }
    };
    initCamera();
    return () => { mounted = false; };
  }, [cameraActive]);

  const stopCamera = async () => {
    await stopQr();
    setCameraActive(false);
  };

  // ---- ARTICLE SEARCH ----
  const searchArticles = useCallback(async (q) => {
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const resp = await api.get('/articles', { params: { search: q } });
      setSearchResults(resp.data.slice(0, 20));
    } catch { setSearchResults([]); }
    setSearching(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchArticles(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery, searchArticles]);

  // ---- ARTICLE LOOKUP ----
  const selectArticle = async (articleId) => {
    try {
      const resp = await api.get(`/stock/article-lookup?article_id=${articleId}`);
      setArticle(resp.data.article);
      setBinsStock(resp.data.bins_stock);
      setTotalStock(resp.data.total_stock);
      setSearchQuery('');
      setSearchResults([]);
      setStep(1);
    } catch {
      toast.error('Eroare la incarcarea articolului');
    }
  };

  const handleScanResult = async (code) => {
    // Parse QR format TYPE:VALUE (e.g., ART:ART-000019, BIN:uuid)
    const parts = code.split(':');
    let searchValue = code;
    let qrType = null;
    if (parts.length >= 2 && ['ART', 'BIN', 'EQ', 'CAB', 'SHELF'].includes(parts[0])) {
      qrType = parts[0];
      searchValue = parts.slice(1).join(':');
    }
    
    toast.info(`Cod scanat: ${code}. Se cauta...`);

    // Handle BIN/CAB/SHELF QR codes - show articles in that location
    if (qrType === 'BIN' || qrType === 'CAB' || qrType === 'SHELF') {
      const scanType = qrType === 'BIN' ? 'bin' : qrType === 'CAB' ? 'cab' : 'shelf';
      if (qrType === 'BIN') {
        try {
          const resp = await api.get(`/bins/${encodeURIComponent(searchValue)}/scan-stock`);
          const data = resp.data;
          if (data.articles_count === 0) {
            toast.error(`Pozitia ${data.bin.cabinet} > ${data.bin.shelf} > ${data.bin.position} este goala`);
            return;
          }
          if (data.articles_count === 1) {
            const art = data.articles[0];
            setArticle({ id: art.article_id, name: art.article_name, code: art.article_code, unit: art.unit, default_bin_id: art.default_bin_id });
            setBinsStock(art.bins_stock || []);
            setTotalStock(art.total_stock);
            setScannedBin(data.bin);
            setBinArticles([]);
            setStep(1);
            toast.success(`Articol unic: ${art.article_name}`);
            return;
          }
          setScannedBin(data.bin);
          setBinArticles(data.articles);
          setStep('bin-select');
          return;
        } catch {
          toast.error(`Pozitie BIN negasita: ${searchValue}`);
          return;
        }
      } else {
        // CAB or SHELF - load location stock
        try {
          let cabinet = '', shelf = '';
          if (qrType === 'CAB') { cabinet = searchValue; }
          else {
            const idx = searchValue.lastIndexOf('_');
            if (idx > 0) { cabinet = searchValue.substring(0, idx); shelf = searchValue.substring(idx + 1); }
            else { cabinet = searchValue; }
          }
          const params = new URLSearchParams({ cabinet });
          if (shelf) params.append('shelf', shelf);
          const resp = await api.get(`/bins-scan-location?${params.toString()}`);
          const data = resp.data;
          if (data.articles_count === 0) {
            toast.error(`${data.location.label} - niciun articol cu stoc`);
            return;
          }
          if (data.articles_count === 1) {
            const art = data.articles[0];
            toast.success(`Articol unic: ${art.article_name}`);
            selectArticle(art.article_id);
            return;
          }
          setScannedBin({ cabinet, shelf: shelf || null, position: null, code: data.location.label });
          setBinArticles(data.articles);
          setStep('bin-select');
          return;
        } catch {
          toast.error(`Locatie negasita: ${searchValue}`);
          return;
        }
      }
    }

    // Handle article QR codes
    try {
      const resp = await api.get(`/stock/article-lookup?q=${encodeURIComponent(searchValue)}`);
      setArticle(resp.data.article);
      setBinsStock(resp.data.bins_stock);
      setTotalStock(resp.data.total_stock);
      setScannedBin(null);
      setBinArticles([]);
      setStep(1);
    } catch {
      // Fallback: try with full raw code
      try {
        const resp2 = await api.get(`/stock/article-lookup?q=${encodeURIComponent(code)}`);
        setArticle(resp2.data.article);
        setBinsStock(resp2.data.bins_stock);
        setTotalStock(resp2.data.total_stock);
        setScannedBin(null);
        setBinArticles([]);
        setStep(1);
      } catch {
        toast.error(`Articol negasit pentru codul scanat. Cauta manual.`);
      }
    }
  };

  // Handle selecting an article from the BIN scan list
  const selectBinArticle = (art) => {
    setArticle({
      id: art.article_id,
      name: art.article_name,
      code: art.article_code,
      unit: art.unit,
      default_bin_id: art.default_bin_id,
    });
    setBinsStock(art.bins_stock || []);
    setTotalStock(art.total_stock);
    setBinArticles([]);
    setStep(1);
  };

  // ---- OPERATION SELECT ----
  const selectOperation = (op) => {
    setOperation(op);
    setQuantity(1);
    setNotes('');
    setFromBinId('');
    setToBinId('');
    // Auto-select first BIN with stock for pick/transfer
    if ((op === 'pick' || op === 'transfer') && binsStock.length > 0) {
      setFromBinId(binsStock[0].bin_id);
    }
    // Auto-select default BIN for add
    if (op === 'add' && article?.default_bin_id) {
      setToBinId(article.default_bin_id);
    }
    setStep(2);
  };

  // ---- SUBMIT ----
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      if (operation === 'pick') {
        await api.post('/stock/issue', {
          article_id: article.id, bin_id: fromBinId, quantity, notes,
          reference_type: 'scan_pick'
        });
        toast.success(`${quantity} ${article.unit} preluate din stoc`);
      } else if (operation === 'add') {
        await api.post('/stock/receive', {
          article_id: article.id, bin_id: toBinId, quantity, notes
        });
        toast.success(`${quantity} ${article.unit} adaugate in stoc`);
      } else if (operation === 'transfer') {
        await api.post('/stock/transfer', {
          article_id: article.id, from_bin_id: fromBinId, to_bin_id: toBinId, quantity, notes
        });
        toast.success(`${quantity} ${article.unit} transferate`);
      }
      // Reset for next operation
      resetWizard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la executare operatiune');
    }
    setSubmitting(false);
  };

  const resetWizard = () => {
    setStep(0);
    setArticle(null);
    setBinsStock([]);
    setTotalStock(0);
    setOperation(null);
    setFromBinId('');
    setToBinId('');
    setQuantity(1);
    setNotes('');
    setScannedBin(null);
    setBinArticles([]);
  };

  // Helpers
  const getBinLabel = (binId) => {
    const b = [...binsStock, ...allBins].find(x => (x.bin_id || x.id) === binId);
    if (!b) return binId;
    return `${b.cabinet || ''} > ${b.shelf || ''} > ${b.position || ''}`;
  };

  const getBinStock = (binId) => {
    const b = binsStock.find(x => x.bin_id === binId);
    return b ? b.quantity : 0;
  };

  const maxQuantity = operation === 'pick' || operation === 'transfer'
    ? Math.round(getBinStock(fromBinId)) : 9999;

  const canSubmit = () => {
    if (quantity <= 0 || quantity > maxQuantity) return false;
    if (operation === 'pick') return !!fromBinId;
    if (operation === 'add') return !!toBinId;
    if (operation === 'transfer') return !!fromBinId && !!toBinId && fromBinId !== toBinId;
    return false;
  };

  const opCfg = operation ? OP_CONFIG[operation] : null;

  return (
    <div className="max-w-2xl mx-auto pb-28 lg:pb-6" data-testid="scan-hub-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl lg:text-3xl font-black text-slate-900 tracking-tight">Operatiuni Stoc</h1>
          <p className="text-sm text-slate-500 mt-0.5">Scanare, preluare, transfer</p>
        </div>
        {(step > 0 || step === 'bin-select') && (
          <Button variant="ghost" size="sm" onClick={resetWizard} data-testid="reset-wizard-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
        )}
      </div>

      {/* Progress */}
      {step !== 0 && step !== 'bin-select' && (
        <div className="flex items-center gap-1 mb-5">
          {['Articol', 'Operatiune', 'Detalii', 'Confirmare'].map((label, i) => (
            <div key={label} className="flex-1">
              <div className={`h-1.5 rounded-full transition-colors ${i < step ? 'bg-blue-600' : i === step ? 'bg-blue-400' : 'bg-slate-200'}`} />
              <p className={`text-[10px] mt-1 text-center font-semibold ${i <= step ? 'text-blue-600' : 'text-slate-400'}`}>{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* ===== STEP 0: SCAN / SEARCH ===== */}
      {step === 0 && (
        <div className="space-y-4">
          {/* Camera Section */}
          <Card className="overflow-hidden border-2 border-slate-200">
            <CardContent className="p-0">
              {cameraActive ? (
                <div className="relative">
                  <div id="stock-qr-reader" style={{ width: '100%', minHeight: '280px' }} />
                  <button
                    className="absolute top-3 right-3 bg-black/60 text-white p-2 rounded-full"
                    onClick={stopCamera}
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className="w-52 h-52 border-2 border-emerald-400/80 rounded-xl">
                      <div className="absolute -top-0.5 -left-0.5 w-6 h-6 border-t-[3px] border-l-[3px] border-emerald-400 rounded-tl-lg" />
                      <div className="absolute -top-0.5 -right-0.5 w-6 h-6 border-t-[3px] border-r-[3px] border-emerald-400 rounded-tr-lg" />
                      <div className="absolute -bottom-0.5 -left-0.5 w-6 h-6 border-b-[3px] border-l-[3px] border-emerald-400 rounded-bl-lg" />
                      <div className="absolute -bottom-0.5 -right-0.5 w-6 h-6 border-b-[3px] border-r-[3px] border-emerald-400 rounded-br-lg" />
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={startCamera}
                  className="w-full flex flex-col items-center justify-center gap-3 py-12 bg-gradient-to-b from-slate-50 to-slate-100 active:from-slate-100 active:to-slate-200 transition-colors"
                  data-testid="start-camera-btn"
                >
                  <div className="w-20 h-20 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-200">
                    <Camera className="w-10 h-10 text-white" />
                  </div>
                  <div className="text-center">
                    <p className="font-bold text-slate-800">Scaneaza Cod QR</p>
                    <p className="text-xs text-slate-500 mt-0.5">Apasa pentru a porni camera</p>
                  </div>
                </button>
              )}
            </CardContent>
          </Card>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 font-medium">SAU CAUTA MANUAL</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cauta articol dupa nume sau cod..."
              className="h-14 pl-11 text-base rounded-xl border-2 border-slate-200 focus:border-blue-500"
              data-testid="article-search-input"
            />
            {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500 animate-spin" />}
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="border-2 border-blue-200 rounded-xl overflow-hidden bg-white shadow-lg">
              {searchResults.map((a) => (
                <button
                  key={a.id}
                  onClick={() => selectArticle(a.id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-blue-50 active:bg-blue-100 transition-colors border-b last:border-b-0"
                  data-testid={`search-result-${a.id}`}
                >
                  <div className="text-left">
                    <p className="font-semibold text-slate-900">{a.name}</p>
                    <p className="text-xs text-slate-500 font-mono mt-0.5">{a.code}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </button>
              ))}
            </div>
          )}

          {/* Upload QR image */}
          <input type="file" accept="image/*" id="qr-file-input" className="hidden" onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            try {
              const el = document.createElement('div');
              el.id = 'temp-qr-reader';
              el.style.display = 'none';
              document.body.appendChild(el);
              const qr = new Html5Qrcode('temp-qr-reader');
              const result = await qr.scanFile(file, true);
              qr.clear();
              el.remove();
              handleScanResult(result);
            } catch { toast.error('Nu s-a putut citi QR din imagine'); }
            e.target.value = '';
          }} />
          <Button variant="outline" className="w-full h-12 rounded-xl" onClick={() => document.getElementById('qr-file-input')?.click()} data-testid="upload-qr-btn">
            <Upload className="w-4 h-4 mr-2" /> Incarca imagine QR
          </Button>
        </div>
      )}

      {/* ===== STEP BIN-SELECT: CHOOSE ARTICLE FROM BIN ===== */}
      {step === 'bin-select' && scannedBin && (
        <div className="space-y-5" data-testid="bin-select-step">
          {/* BIN Header */}
          <Card className="border-2 border-purple-200 bg-purple-50/50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-purple-600 flex items-center justify-center shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-lg text-slate-900">
                    {scannedBin.cabinet} &gt; {scannedBin.shelf} &gt; {scannedBin.position}
                  </p>
                  <p className="text-sm text-slate-500 font-mono">{scannedBin.code}</p>
                  <Badge className="bg-purple-600 text-white mt-1">
                    {binArticles.length} articole in aceasta pozitie
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Selectati articolul:</p>

          {/* Articles list */}
          <div className="space-y-2">
            {binArticles.map((art) => (
              <button
                key={art.article_id}
                onClick={() => selectBinArticle(art)}
                className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50 active:bg-blue-100 transition-all"
                data-testid={`bin-article-${art.article_id}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                    <Package className="w-5 h-5 text-slate-500" />
                  </div>
                  <div className="text-left min-w-0">
                    <p className="font-bold text-sm text-slate-900 truncate">{art.article_name}</p>
                    <p className="text-xs text-slate-500 font-mono">{art.article_code}</p>
                    {art.supplier_name && (
                      <p className="text-xs text-slate-400">Furnizor: {art.supplier_name}</p>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <p className="text-lg font-black text-blue-700">{Math.round(art.quantity_in_bin)}</p>
                  <p className="text-[10px] text-slate-500">{art.unit} in pozitie</p>
                </div>
              </button>
            ))}
          </div>

          <Button variant="ghost" className="w-full" onClick={resetWizard}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Scanare noua
          </Button>
        </div>
      )}

      {/* ===== STEP 1: SELECT OPERATION ===== */}
      {step === 1 && article && (
        <div className="space-y-5">
          {/* Article Card */}
          <Card className="border-2 border-blue-200 bg-blue-50/50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
                  <Package className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-lg text-slate-900 truncate">{article.name}</p>
                  <p className="text-sm text-slate-500 font-mono">{article.code}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <Badge className="bg-blue-600 text-white text-sm px-3">
                      Stoc Total: {Math.round(totalStock)} {article.unit}
                    </Badge>
                    {article.category_name && <Badge variant="outline">{article.category_name}</Badge>}
                  </div>
                </div>
              </div>
              {/* Stock per BIN */}
              {binsStock.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {binsStock.map((b) => (
                    <div key={b.bin_id} className="flex items-center justify-between px-3 py-2 bg-white rounded-lg text-sm">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-slate-700">{b.cabinet} &gt; {b.shelf} &gt; {b.position}</span>
                      </div>
                      <span className="font-bold text-slate-900">{Math.round(b.quantity)} {article.unit}</span>
                    </div>
                  ))}
                </div>
              )}
              {binsStock.length === 0 && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span className="text-sm text-amber-700">Niciun stoc inregistrat</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Operation Buttons */}
          <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Ce doriti sa faceti?</p>

          <button
            onClick={() => selectOperation('pick')}
            className="w-full flex items-center gap-4 p-5 rounded-xl border-2 border-red-200 bg-white hover:bg-red-50 active:bg-red-100 transition-colors"
            data-testid="op-pick-btn"
            disabled={totalStock === 0}
          >
            <div className="w-14 h-14 rounded-xl bg-red-600 flex items-center justify-center shrink-0">
              <ArrowUp className="w-7 h-7 text-white" />
            </div>
            <div className="text-left flex-1">
              <p className="font-bold text-lg text-slate-900">Preluare din Stoc</p>
              <p className="text-sm text-slate-500">Scoate cantitate dintr-o locatie</p>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-300" />
          </button>

          <button
            onClick={() => selectOperation('transfer')}
            className="w-full flex items-center gap-4 p-5 rounded-xl border-2 border-blue-200 bg-white hover:bg-blue-50 active:bg-blue-100 transition-colors"
            data-testid="op-transfer-btn"
            disabled={totalStock === 0}
          >
            <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
              <ArrowLeftRight className="w-7 h-7 text-white" />
            </div>
            <div className="text-left flex-1">
              <p className="font-bold text-lg text-slate-900">Transfer</p>
              <p className="text-sm text-slate-500">Muta cantitate intre doua locatii</p>
            </div>
            <ChevronRight className="w-6 h-6 text-slate-300" />
          </button>

          <Button variant="ghost" className="w-full" onClick={() => setStep(0)}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Alege alt articol
          </Button>
        </div>
      )}

      {/* ===== STEP 2: CONFIGURE ===== */}
      {step === 2 && article && opCfg && (
        <div className="space-y-5">
          {/* Op Header */}
          <div className={`flex items-center gap-3 p-4 rounded-xl border ${opCfg.lightBg}`}>
            <div className={`w-10 h-10 rounded-lg ${opCfg.color} flex items-center justify-center`}>
              <opCfg.icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className={`font-bold ${opCfg.textColor}`}>{opCfg.label}</p>
              <p className="text-xs text-slate-500">{article.name} ({article.code})</p>
            </div>
          </div>

          {/* FROM BIN (pick / transfer) */}
          {(operation === 'pick' || operation === 'transfer') && (
            <div className="space-y-2">
              <Label className="font-semibold">Locatie Sursa *</Label>
              <div className="space-y-2">
                {binsStock.map((b) => (
                  <button
                    key={b.bin_id}
                    onClick={() => { setFromBinId(b.bin_id); if (operation === 'pick') setQuantity(Math.min(quantity, b.quantity)); }}
                    className={`w-full flex items-center justify-between p-3.5 rounded-xl border-2 transition-all ${
                      fromBinId === b.bin_id
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    data-testid={`from-bin-${b.bin_id}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <MapPin className={`w-4 h-4 ${fromBinId === b.bin_id ? 'text-blue-600' : 'text-slate-400'}`} />
                      <span className="font-medium text-sm">{b.cabinet} &gt; {b.shelf} &gt; {b.position}</span>
                    </div>
                    <Badge variant={fromBinId === b.bin_id ? 'default' : 'secondary'} className="text-sm">
                      {Math.round(b.quantity)} {article.unit}
                    </Badge>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* TO BIN (add / transfer) */}
          {(operation === 'add' || operation === 'transfer') && (
            <div className="space-y-2">
              <Label className="font-semibold">{operation === 'transfer' ? 'Locatie Destinatie *' : 'Locatie *'}</Label>
              <Select value={toBinId} onValueChange={setToBinId}>
                <SelectTrigger className="h-12 rounded-xl" data-testid="to-bin-select">
                  <SelectValue placeholder="Selecteaza locatia..." />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {allBins.filter(b => b.id !== fromBinId).map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.cabinet} &gt; {b.shelf} &gt; {b.position} ({b.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* QUANTITY */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">Cantitate *</Label>
              {(operation === 'pick' || operation === 'transfer') && fromBinId && (
                <span className="text-xs text-slate-500">Disponibil: <span className="font-bold">{Math.round(getBinStock(fromBinId))} {article.unit}</span></span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                className="w-14 h-14 rounded-xl border-2 border-slate-200 bg-white flex items-center justify-center text-slate-700 hover:bg-slate-50 active:bg-slate-100 font-bold text-xl"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                data-testid="qty-minus"
              >
                <Minus className="w-6 h-6" />
              </button>
              <div className="flex-1 h-14 rounded-xl border-2 border-blue-300 bg-blue-50 flex items-center justify-center">
                <span className="text-2xl font-black text-blue-700" data-testid="qty-value">{quantity}</span>
                <span className="text-sm text-blue-500 ml-2">{article.unit}</span>
              </div>
              <button
                className="w-14 h-14 rounded-xl border-2 border-slate-200 bg-white flex items-center justify-center text-slate-700 hover:bg-slate-50 active:bg-slate-100 font-bold text-xl"
                onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                data-testid="qty-plus"
              >
                <Plus className="w-6 h-6" />
              </button>
            </div>
            {quantity > maxQuantity && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Depaseste stocul disponibil
              </p>
            )}
          </div>

          {/* NOTES */}
          <div className="space-y-2">
            <Label>Note (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Motiv, referinta..."
              rows={2}
              className="rounded-xl"
              data-testid="op-notes"
            />
          </div>

          {/* Navigation */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" className="flex-1 h-12 rounded-xl" onClick={() => setStep(1)}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Inapoi
            </Button>
            <Button
              className={`flex-1 h-12 rounded-xl ${opCfg.color} ${opCfg.hoverColor}`}
              disabled={!canSubmit()}
              onClick={() => setStep(3)}
              data-testid="go-to-confirm"
            >
              Continua <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* ===== STEP 3: CONFIRM ===== */}
      {step === 3 && article && opCfg && (
        <div className="space-y-5">
          <div className="text-center mb-2">
            <div className={`w-16 h-16 rounded-2xl ${opCfg.color} flex items-center justify-center mx-auto mb-3`}>
              <opCfg.icon className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Confirmare {opCfg.label}</h2>
          </div>

          <Card className="border-2">
            <CardContent className="p-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Articol</span>
                <span className="font-bold text-right">{article.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Cod</span>
                <span className="font-mono">{article.code}</span>
              </div>
              <div className="h-px bg-slate-100" />
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Cantitate</span>
                <span className={`font-black text-lg ${opCfg.textColor}`}>{quantity} {article.unit}</span>
              </div>
              {(operation === 'pick' || operation === 'transfer') && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Din locatia</span>
                  <span className="font-semibold">{getBinLabel(fromBinId)}</span>
                </div>
              )}
              {(operation === 'add' || operation === 'transfer') && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">In locatia</span>
                  <span className="font-semibold">{getBinLabel(toBinId)}</span>
                </div>
              )}
              {notes && (
                <>
                  <div className="h-px bg-slate-100" />
                  <div className="text-sm">
                    <span className="text-slate-500">Note: </span>
                    <span className="text-slate-700">{notes}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1 h-12 rounded-xl" onClick={() => setStep(2)}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Modifica
            </Button>
            <Button
              className={`flex-1 h-14 rounded-xl text-base font-bold ${opCfg.color} ${opCfg.hoverColor}`}
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="confirm-operation-btn"
            >
              {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Check className="w-5 h-5 mr-2" />}
              {submitting ? 'Se proceseaza...' : 'EXECUTA'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScanHubPage;
